/* eslint-disable import/no-deprecated */
import { intl } from '@fullstory/intl';
import { isMidnight } from '@fullstory/util/date';
import moment, { Moment } from 'moment';
import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Text } from '../../../../components/Text';
import { Tooltip } from '../../../../components/Tooltip';
import { Icon } from '../../../../deprecated/Icon';
import { Box } from '../../../Box';
import { DateRangePickerContext } from '../../context';
import { FORMAT_LONG, FORMAT_LONG_DAY, FORMAT_LONG_TIME } from './constants';

const LOCAL_DATE_FORMAT_STRING = moment.localeData().longDateFormat(FORMAT_LONG_DAY);
const LOCAL_TIME_FORMAT_STRING = moment.localeData().longDateFormat(FORMAT_LONG_TIME);
// Parse date in English and convert to users locale
const exampleDate = moment('12/23/2021 17:05', 'MM/DD/YYYY H:mm', 'en', true).locale(intl.locale);
const exampleDateNoTime = moment('12/23/2021', 'MM/DD/YYYY', 'en', true).locale(intl.locale);

interface RightAdornmentProps {
  date: Moment | null;
  focused: boolean;
  onClick: () => void;
}

export const RightAdornment: React.FC<RightAdornmentProps> = ({ focused, date, onClick }) => {
  const intl = useIntl();
  const { timeDisplayMode } = useContext(DateRangePickerContext);
  const enableTimeTip = timeDisplayMode === 'always' || timeDisplayMode === 'auto';

  // input should be adorned with a formatting tooltip if focused
  if (focused) {
    return (
      <Box marginRight={4}>
        <Tooltip
          tooltipContent={
            <>
              <Text appearance="bodySmall" color="default-inverse">
                <FormattedMessage
                  defaultMessage="Dates and times can be entered using the format:"
                  id="zmwDBR"
                />
              </Text>
              <Text appearance="bodySmallBold" color="default-inverse">
                {LOCAL_DATE_FORMAT_STRING}
              </Text>
              {enableTimeTip && (
                <Text appearance="bodySmallBold" color="default-inverse">
                  {`${LOCAL_DATE_FORMAT_STRING} ${LOCAL_TIME_FORMAT_STRING}`}
                </Text>
              )}
              <Text appearance="bodySmall" color="default-inverse">
                {`${intl.formatMessage({
                  defaultMessage: 'e.g.',
                  id: 'Z8ffZh',
                  description: 'Stands for exempli gratia in Latin meaning "for example"',
                })} ${
                  enableTimeTip
                    ? exampleDate.format(FORMAT_LONG)
                    : exampleDateNoTime.format(FORMAT_LONG_DAY)
                }`}
              </Text>
            </>
          }
        >
          <Icon name="information-minimal" color="neutrals-n8" size={16} />
        </Tooltip>
      </Box>
    );
  }

  // If timeDisplayMode is set to "never" or "onfocus", the time should not be displayed
  // because we're not currently focused
  if (timeDisplayMode === 'never' || timeDisplayMode === 'onfocus') {
    return null;
  }

  // input should be adorned with a timestamp if the time is not midnight
  // or if the timeDisplayMode is set to "always"
  return date && date.isValid() && (!isMidnight(date) || timeDisplayMode === 'always') ? (
    <Box data-component="RightAdornment" marginRight={8} onClick={onClick}>
      <Text appearance="bodySmall" color="secondary">
        {date.format(FORMAT_LONG_TIME)}
      </Text>
    </Box>
  ) : null;
};
